import { useMemo } from "react";

import { useSelector } from "react-redux";

import { pluralize } from "lib/pluralize";

import {
  columnSignature,
  selectDataSetCountByMappingIdLookup,
  selectSideLoadedDataMappingIdByColumnSignatureLookup,
  selectSideLoadedDataMappingsList,
} from "selectors";

export const NEW_TEMPLATE = "";

export function useTemplateOptions(sourceColumns: string[]): {
  templates: SideLoadedDataMapping[];
  templateOptions: { label: string; value: string }[];
  autoMatchedId: string | undefined;
  templateIdBySignature: Record<string, string>;
  dataSetCountByTemplateId: Record<string, number>;
} {
  const templates = useSelector(selectSideLoadedDataMappingsList);
  const templateIdBySignature = useSelector(
    selectSideLoadedDataMappingIdByColumnSignatureLookup,
  );
  const dataSetCountByTemplateId = useSelector(
    selectDataSetCountByMappingIdLookup,
  );

  // Re-derived rather than stashed, so renaming or deleting a template while
  // the dialog is open doesn't leave a stale match.
  const autoMatchedId = templateIdBySignature[columnSignature(sourceColumns)];

  const templateOptions = useMemo(
    () =>
      [{ label: "Create new template", value: NEW_TEMPLATE }].concat(
        templates.map(template => {
          const usedBy = dataSetCountByTemplateId[template.id] || 0;
          const notes = [
            template.id === autoMatchedId
              ? "matches this file's columns"
              : null,
            usedBy
              ? `used by ${usedBy} data ${pluralize("set", usedBy)}`
              : null,
          ].filter(Boolean);
          return {
            label: notes.length
              ? `${template.name} (${notes.join(", ")})`
              : template.name,
            value: template.id,
          };
        }),
      ),
    [templates, dataSetCountByTemplateId, autoMatchedId],
  );

  return {
    templates,
    templateOptions,
    autoMatchedId,
    templateIdBySignature,
    dataSetCountByTemplateId,
  };
}
